import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import GoldButton from '../components/GoldButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Shield, Trash2, Plus, KeyRound } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminManagement() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre: '',
    email: ''
  });
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/usuarios?limit=100');
      // La respuesta ahora tiene formato: { data: [...], total, skip, limit, has_more }
      const adminsData = response.data.data || response.data;
      setAdmins(adminsData);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acceso denegado. Solo superadmin puede ver esta seccion.');
      } else {
        toast.error('Error al cargar administradores');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admin/usuarios/crear', formData);
      toast.success('Administrador creado exitosamente');
      setShowCreateModal(false);
      setFormData({ username: '', password: '', nombre: '', email: '' });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear administrador');
    }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Estas seguro de eliminar al administrador ${name}?`)) return;
    try {
      await api.delete(`/admin/usuarios/${userId}`);
      toast.success('Administrador eliminado');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await api.put(`/admin/usuarios/${userId}`, { active: !currentStatus });
      toast.success(`Administrador ${!currentStatus ? 'activado' : 'desactivado'}`);
      fetchAdmins();
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const openResetModal = (admin) => {
    setResetTarget(admin);
    setNewPassword('');
    setShowResetModal(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!resetTarget) return;
    if (newPassword.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setResetting(true);
    try {
      await api.put(`/admin/usuarios/${resetTarget.user_id}`, { password: newPassword });
      toast.success(`Contraseña restablecida para ${resetTarget.name}`);
      setShowResetModal(false);
      setResetTarget(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al restablecer contraseña');
    } finally {
      setResetting(false);
    }
  };

  const handleSanear = async () => {
    if (!window.confirm('Esto dejará SOLO los 3 administradores autorizados (lista blanca) y eliminará el resto. ¿Continuar?')) return;
    try {
      const res = await api.post('/admin/usuarios/sanear-allowlist');
      toast.success(`Allowlist aplicada: ${res.data.eliminados_count} eliminados, ${res.data.retenidos?.length || 0} retenidos`);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al sanear usuarios');
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[#F5A623] text-xl">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl text-white font-bold mb-2" data-testid="admin-management-title">Gestion de Administradores</h3>
          <p className="text-[#666666]">Control total de accesos del sistema (Solo Superadmin)</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleSanear}
            className="px-4 py-2 bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30 rounded-full hover:bg-[#EF4444]/30 transition-colors text-sm font-semibold flex items-center gap-2"
            data-testid="sanear-allowlist-button"
            title="Dejar solo los administradores autorizados (lista blanca)"
          >
            <Shield className="w-4 h-4" />
            Sanear a lista blanca
          </button>
          <GoldButton onClick={() => setShowCreateModal(true)} variant="primary" testId="create-admin-button">
            <Plus className="w-5 h-5 mr-2 inline" />
            Crear Administrador
          </GoldButton>
        </div>
      </div>

      <div className="grid gap-4">
        {admins.map((admin) => (
          <div
            key={admin.user_id}
            className="bg-[#2A2A2A] border border-white/10 rounded-lg p-6 hover:border-[#F5A623]/50 transition-colors"
            data-testid={`admin-card-${admin.user_id}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4 flex-1">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  admin.role === 'superadmin' ? 'bg-[#EF4444]/20' : 'bg-[#3B82F6]/20'
                }`}>
                  <Shield className={`w-6 h-6 ${
                    admin.role === 'superadmin' ? 'text-[#EF4444]' : 'text-[#3B82F6]'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-white font-bold text-lg">{admin.name}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      admin.role === 'superadmin'
                        ? 'bg-[#EF4444]/20 text-[#EF4444] border border-[#EF4444]/30'
                        : 'bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/30'
                    }`}>
                      {admin.role === 'superadmin' ? 'SUPERADMIN' : 'ADMIN'}
                    </span>
                    <span
                      data-testid={`admin-status-${admin.user_id}`}
                      className={`px-3 py-1 rounded-full text-xs font-bold border ${
                        admin.active
                          ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30'
                          : 'bg-[#666666]/20 text-[#999999] border-[#666666]/30'
                      }`}
                    >
                      {admin.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </div>
                  {admin.username && <p className="text-[#666666] text-sm mb-1">@{admin.username}</p>}
                  <p className="text-[#666666] text-sm">{admin.email}</p>
                </div>
              </div>

              {admin.role !== 'superadmin' && (
                <div className="flex gap-2">
                  <button
                    onClick={() => openResetModal(admin)}
                    className="px-4 py-2 bg-[#8B5CF6]/20 text-[#8B5CF6] rounded-lg hover:bg-[#8B5CF6]/30 transition-colors text-sm font-semibold flex items-center gap-1"
                    data-testid={`reset-password-btn-${admin.user_id}`}
                    title="Restablecer contraseña"
                  >
                    <KeyRound className="w-4 h-4" />
                    Restablecer Contraseña
                  </button>
                  <button
                    onClick={() => handleToggleActive(admin.user_id, admin.active)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      admin.active
                        ? 'bg-[#F59E0B]/20 text-[#F59E0B] hover:bg-[#F59E0B]/30'
                        : 'bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30'
                    }`}
                    data-testid={`toggle-active-${admin.user_id}`}
                  >
                    {admin.active ? 'Desactivar' : 'Activar'}
                  </button>
                  <button
                    onClick={() => handleDelete(admin.user_id, admin.name)}
                    className="px-4 py-2 bg-[#EF4444]/20 text-[#EF4444] rounded-lg hover:bg-[#EF4444]/30 transition-colors text-sm font-semibold"
                    data-testid={`delete-admin-${admin.user_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}

              {admin.role === 'superadmin' && (
                <TooltipProvider delayDuration={150}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        data-testid={`superadmin-protected-badge-${admin.user_id}`}
                        className="flex items-center gap-2 px-4 py-2 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg text-[#EF4444] text-sm font-bold cursor-help"
                      >
                        <Shield className="w-4 h-4" />
                        Superadmin / Protegido
                      </span>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs bg-[#1A1A1A] text-white border border-[#EF4444]/30">
                      Cuenta del sistema protegida contra eliminación y modificación de rol
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#1A1A1A] border border-[#F5A623]/30 rounded-2xl p-8 max-w-md">
          <DialogTitle className="text-2xl font-bold text-white mb-6">Crear Nuevo Administrador</DialogTitle>
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <Label className="text-white text-sm uppercase mb-2 block">Username</Label>
              <Input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                required
                className="bg-[#2A2A2A] border-white/20 text-white"
                placeholder="usuario123"
                data-testid="create-admin-username"
              />
            </div>
            <div>
              <Label className="text-white text-sm uppercase mb-2 block">Nombre Completo</Label>
              <Input
                type="text"
                value={formData.nombre}
                onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                required
                className="bg-[#2A2A2A] border-white/20 text-white"
                placeholder="Juan Perez"
                data-testid="create-admin-nombre"
              />
            </div>
            <div>
              <Label className="text-white text-sm uppercase mb-2 block">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="bg-[#2A2A2A] border-white/20 text-white"
                placeholder="admin@kompa.global"
                data-testid="create-admin-email"
              />
            </div>
            <div>
              <Label className="text-white text-sm uppercase mb-2 block">Contrasena</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required
                minLength={8}
                className="bg-[#2A2A2A] border-white/20 text-white"
                placeholder="Min 8 caracteres"
                data-testid="create-admin-password"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <GoldButton type="button" variant="ghost" onClick={() => setShowCreateModal(false)} className="flex-1">
                Cancelar
              </GoldButton>
              <GoldButton type="submit" variant="primary" className="flex-1" testId="confirm-create-admin">
                Crear
              </GoldButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal para restablecer contraseña */}
      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="bg-gradient-to-br from-[#2A2A2A] to-[#1A1A1A] border border-[#8B5CF6]/30 rounded-2xl p-8 max-w-md" data-testid="reset-password-modal">
          <DialogTitle className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <KeyRound className="w-6 h-6 text-[#8B5CF6]" />
            Restablecer Contraseña
          </DialogTitle>
          <DialogDescription className="text-[#999999] mb-6">
            Define una nueva contraseña para <span className="text-white font-semibold">{resetTarget?.name}</span>
            {resetTarget?.username && <span className="text-[#666666]"> (@{resetTarget.username})</span>}. El usuario deberá iniciar sesión con la nueva contraseña.
          </DialogDescription>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label className="text-white text-sm uppercase mb-2 block">Nueva Contraseña</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="bg-[#2A2A2A] border-white/20 text-white"
                placeholder="Min 8 caracteres"
                data-testid="reset-password-input"
              />
            </div>
            <div className="flex gap-3 pt-4">
              <GoldButton type="button" variant="ghost" onClick={() => setShowResetModal(false)} className="flex-1">
                Cancelar
              </GoldButton>
              <button
                type="submit"
                disabled={resetting}
                className="flex-1 px-6 py-3 bg-[#8B5CF6] text-white rounded-full hover:bg-[#7C3AED] transition-colors font-bold disabled:opacity-50"
                data-testid="reset-password-admin-btn"
              >
                {resetting ? 'Guardando...' : 'Restablecer'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
